import { Button, HStack, Text } from '@chakra-ui/react'
import { ButtonLink } from '@src/components/sco/ButtonLink'
import { usePathname, useSearchParams } from 'next/navigation'
import React from 'react'

export type GridPaginationProps = {
  page: number
  totalPages: number
  /** Query-string key used to track the current page. Defaults to `page`. */
  pageParam?: string
}

/**
 * Page-based pagination rendered as real links (ButtonLink) so pages are
 * crawlable, shareable, and support middle-click / back-forward. The active
 * page is tracked in the `page` query param (read via useQueryState in the grids).
 */
export const GridPagination = ({ page, totalPages, pageParam = 'page' }: GridPaginationProps) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  if (totalPages <= 1) {
    return null
  }

  const hrefFor = (target: number) => {
    const params = new URLSearchParams(searchParams?.toString())
    if (target <= 1) {
      params.delete(pageParam)
    } else {
      params.set(pageParam, String(target))
    }
    const qs = params.toString()
    return qs ? `${pathname}?${qs}` : pathname
  }

  const hasPrev = page > 1
  const hasNext = page < totalPages

  return (
    <HStack gap={4} justify={'center'} mt={10}>
      {hasPrev ? (
        <ButtonLink href={hrefFor(page - 1)} size={'sm'} variant={'outline'}>
          ← Previous
        </ButtonLink>
      ) : (
        <Button disabled size={'sm'} variant={'outline'}>
          ← Previous
        </Button>
      )}

      <Text color={'fg.muted'} flexShrink={0} textStyle={'sm'}>
        Page {page} of {totalPages}
      </Text>

      {hasNext ? (
        <ButtonLink href={hrefFor(page + 1)} size={'sm'} variant={'outline'}>
          Next →
        </ButtonLink>
      ) : (
        <Button disabled size={'sm'} variant={'outline'}>
          Next →
        </Button>
      )}
    </HStack>
  )
}
